import React from 'react';

import { button, icon, text } from '@harmony/enablers/react';

import { HealthStatus, ServiceHealthPageType } from '../../../Common/Constants';
import { getHarmonyScope } from '../../../Services/Services.Secondary';
import LocalizationService from '../../../Services/Services.Secondary/Localization/LocalizationService';
import { ServiceStatus } from './Models/ServiceHealthTables';

const Button = getHarmonyScope().forReact(button);
const Icon = getHarmonyScope().forReact(icon);
const Text = getHarmonyScope().forReact(text);

const resx = LocalizationService.getInstance();

const contentMaxLength = 200;
const goodIconStyle = 'success';
const goodIconName = 'completed';
const informationIconName = 'error';
const errorIconName = 'errorbadge';
const warningIconName = 'warning';
const goodHealthMessage = resx.getLocalizedResource('ServiceHealthGood_text');
const informationMessage = resx.getLocalizedResource('ServiceHealthInformation_text');
const warningMessage = resx.getLocalizedResource('ServiceHealthWarning_text');
const errorMessage = resx.getLocalizedResource('ServiceHealthError_text');
const viewMoreButtonServiceAnnouncementMessage_text = resx.getLocalizedResource(
  'ViewMoreButtonServiceAnnouncementMessage_text'
);
const showLessButtonServiceAnnouncementMessage_text = resx.getLocalizedResource(
  'ShowLessButtonServiceAnnouncementMessage_text'
);

type StatusTableRowProps = {
  serviceHealth: ServiceStatus;
  id: number;
  serviceHealthPageType: number;
};

type StatusTableRowState = {
  showFullDescription: boolean;
};

export default class StatusTableRow extends React.Component<StatusTableRowProps, StatusTableRowState> {

  constructor(props: StatusTableRowProps) {
    super(props);
    this.state = {
      showFullDescription: false,
    };
  }

  private getStatusMessage = (healthStatus: number) => {
    return healthStatus === HealthStatus.Good.Code
      ? goodHealthMessage
      : healthStatus === HealthStatus.Information.Code
      ? informationMessage
      : healthStatus === HealthStatus.Warning.Code
      ? warningMessage
      : errorMessage;
  };

  private getStatusIconName = (healthStatus: number) => {
    switch (healthStatus) {
      case HealthStatus.Good.Code:
        return goodIconName;
      case HealthStatus.Warning.Code:
        return warningIconName;
      case HealthStatus.Information.Code:
        return informationIconName;
      case HealthStatus.Error.Code:
        return errorIconName;
    }
    return goodIconName;
  };

  private getStatusKey = (healthsStatus: number) => {
    return healthsStatus === HealthStatus.Good.Code
      ? HealthStatus.Good.Key
      : healthsStatus === HealthStatus.Information.Code
      ? HealthStatus.Information.Key
      : healthsStatus === HealthStatus.Warning.Code
      ? HealthStatus.Warning.Key
      : HealthStatus.Error.Key;
  };

  private getStatusIconReact = (status: number) => {
    const statusIconName: string = this.getStatusIconName(status);;
    const statusStyle: string =
      this.props.serviceHealth.status === HealthStatus.Good.Code
        ? goodIconStyle
        : this.getStatusKey(this.props.serviceHealth.status).toLowerCase();
        return  <Icon name={statusIconName as any} className={'status-' + statusStyle}></Icon>;
  }

  private getStatusIconV1 = (status: number) => {

  
    let iconClassName = "";
    let colorClassName = "";
  
    switch (status) {
      case 1:
        iconClassName = "win-icon-ErrorBadge";
        colorClassName = "win-color-fg-red";
        break;
      case 2:
        iconClassName = "win-icon-Warning";
        colorClassName = "win-color-fg-yellow";
        break;
      case 3:
        iconClassName = "win-icon-Error";
        colorClassName = "win-color-fg-blue";
        break;
      default:
        iconClassName = "win-icon-Completed";
        colorClassName = "win-color-fg-green";
        break;
    }
  
    return (
      <div className="baseV1Icon">
        <span className={`win-icon win-icon-size-md ${iconClassName} ${colorClassName}`}></span>
      </div>
    );
  }
  
  private getStatusDiv = (status: number) => {
    const statusIcon: React.ReactNode = (this.props.serviceHealthPageType === ServiceHealthPageType.Public ? this.getStatusIconV1(status): this.getStatusIconReact(status));    
    
    const iconMessage: string = this.getStatusMessage(status);
    return (
      <span slot={`status-${this.props.id}`}>
        <Text>
          {statusIcon} &nbsp;{iconMessage}
        </Text>
      </span>
    );
  };

  private changeFullDescription = () => {
    this.setState({ showFullDescription: !this.state.showFullDescription });
  };

  private getDescriptionElement = () => {
    const descriptionText =
      this.props.serviceHealth.status === HealthStatus.Good.Code || !this.props.serviceHealth.statusDescription
        ? ''
        : this.props.serviceHealth.statusDescription;
      const isLongDescription = descriptionText.length > contentMaxLength;
      const truncatedDescription =
        isLongDescription && !this.state.showFullDescription
          ? `${descriptionText.substring(0, descriptionText.substring(0, contentMaxLength).lastIndexOf(' '))}`
          : descriptionText;
    return (
      <span slot={`statusDescription-${this.props.id}`}>
        <div className="description-container">
          <div>
            <Text>{truncatedDescription}</Text>
          </div>
          {isLongDescription && (
            <div className="description-expand-button">
              <Button
                appearance="stealth"
                onClick={this.changeFullDescription}
                aria-label={
                  this.state.showFullDescription
                    ? showLessButtonServiceAnnouncementMessage_text
                    : viewMoreButtonServiceAnnouncementMessage_text
                }
              >
                <Icon slot="start" name={this.state.showFullDescription ? 'chevronup' : 'chevrondown'} />
              </Button>
            </div>
          )}
        </div>
      </span>
    );
  };

  public render() {
    if (this.props.serviceHealth && this.props.serviceHealth.status && this.props.serviceHealth.name) {
      return (
        <>
          {this.props.serviceHealth.visible && <span slot={`workspace-${this.props.id}`}>
            <Text>{this.props.serviceHealth.workspace}</Text>
          </span>}
          {this.props.serviceHealthPageType !== ServiceHealthPageType.Admin && <span slot={`name-${this.props.id}`}>
            <Text>{this.props.serviceHealth.name}</Text>
          </span>}
          {this.getStatusDiv(this.props.serviceHealth.status)}
          {this.getDescriptionElement()}
        </>
      );
    }

    return null;
  }
}
